import type { UsageMetricSet, UsageTrendMetric } from './types';

const USAGE_COST_LOCALE = 'is-IS';

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat(USAGE_COST_LOCALE, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value || 0);

export const formatCostPerMinute = (value: number) =>
  new Intl.NumberFormat(USAGE_COST_LOCALE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value || 0);

export const formatNumber = (value: number) => new Intl.NumberFormat(USAGE_COST_LOCALE).format(Math.round(value || 0));

export const formatDecimal = (value: number, digits = 2) =>
  new Intl.NumberFormat(USAGE_COST_LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value || 0);

export const formatDate = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString(USAGE_COST_LOCALE, {
    month: 'short',
    day: 'numeric',
  });

const truncateMiddle = (value: string, maxLength: number) => {
  if (value.length <= maxLength) return value;

  const keepStart = Math.ceil((maxLength - 3) / 2);
  const keepEnd = Math.floor((maxLength - 3) / 2);
  return `${value.slice(0, keepStart)}...${value.slice(-keepEnd)}`;
};

export const formatBreakdownAxisLabel = (value: string, maxLength = 28) => {
  if (value.length <= maxLength) return value;

  const separatorIndex = value.indexOf(':');
  if (separatorIndex <= 0) return truncateMiddle(value, maxLength);

  const provider = value.slice(0, separatorIndex);
  const model = value.slice(separatorIndex + 1);
  const modelLength = maxLength - provider.length - 1;

  if (modelLength < 8) return truncateMiddle(value, maxLength);
  return `${provider}:${truncateMiddle(model, modelLength)}`;
};

export const getQualityGapCount = (
  metrics: Pick<UsageMetricSet, 'missing_cost_events' | 'missing_audio_duration_events'>
) => metrics.missing_cost_events + metrics.missing_audio_duration_events;

export const getTrendValueKey = (metric: UsageTrendMetric) => {
  switch (metric) {
    case 'cost_per_audio_minute':
      return 'total_cost_per_audio_minute';
    case 'events':
      return 'total_events';
    case 'data_gaps':
      return 'data_gaps';
    default:
      return 'total_cost_usd';
  }
};

export const getTrendLabel = (metric: UsageTrendMetric) => {
  switch (metric) {
    case 'cost_per_audio_minute':
      return 'Kostnaður á hljóðmínútu';
    case 'events':
      return 'Atburðir';
    case 'data_gaps':
      return 'Gagnagöt';
    default:
      return 'Heildarkostnaður';
  }
};

export const formatTrendValue = (value: number, metric: UsageTrendMetric) => {
  if (metric === 'cost') return formatCurrency(value);
  if (metric === 'cost_per_audio_minute') return formatCostPerMinute(value);
  return formatNumber(value);
};
